// * CONTEXT ---------------------------------------------------------------------------------------

import { TFetchUserTokenParams } from '~config/serverEndpoints'
import { TMachineTypes } from '~logic/xstate/typings'

import { TFetchTokenResponse } from '../utils/fetchCustomerToken'

export const GUEST_ID = '1'

type TBaseContext = {
  userId: NullableID
  userToken: Nullable<string>
  guestToken: Nullable<string>
  error: Nullable<Error>
}

// empty context for invalid state
type TEmptyContext = {
  [key in keyof TBaseContext]: null
}

// valid guest context
type TGuestContext = {
  userId: typeof GUEST_ID
  userToken: string
  guestToken: string
  error: TBaseContext[`error`]
}

// valid user context
type TUserContext = {
  userId: Defined<TBaseContext['userId']>
  userToken: Defined<TBaseContext['userToken']>
  guestToken: Defined<TBaseContext['guestToken']>
  error: null
}

// * STATES ----------------------------------------------------------------------------------------

type TStateContextMap = {
  // initial state, where the initial context is loaded (eg. from persistence)
  init: TBaseContext
  // state to validate if there is the valid context or not
  // if the context is not valid, it transitions to the 'guest' state node
  // to obtain the guest auth token
  validate: TBaseContext
  // guest auth token is obtained in this state
  // if successful, the machine transitions to the 'valid' state
  // otherwise it transitions to the 'failed' state
  logGuest: TBaseContext
  // user (registered customer) auth token is obtained in this state
  // if successful, the machine transitions to the 'valid' state
  // otherwise it transitions to the 'failed' state
  logUser: TBaseContext
  // we have the valid guest context in this state node
  guest: TGuestContext
  // we have the valid user context in this state node
  user: TUserContext
  // attempt to obtain either user or guest auth token has failed
  failed: TEmptyContext & {
    error: TBaseContext[`error`]
  }
}

//* EVENTS -----------------------------------------------------------------------------------------

// events created by the machine itself
// as a reaction to a success or a failure of invoked services
type TInternalEvents =
  | {
      type: 'done.invoke.obtainGuestToken'
      data: TFetchTokenResponse
    }
  | {
      type: 'error.platform.obtainGuestToken'
      data: Error
    }
  | {
      type: 'done.invoke.obtainUserToken'
      data: TFetchTokenResponse
    }
  | {
      type: 'error.platform.obtainUserToken'
      data: Error
    }
  | {
      type: 'done.invoke.initContext'
      data: Nullable<TBaseContext>
    }
  | {
      type: 'error.platform.initContext'
      data: Error
    }

// try to obtain the user auth token
// using the `username` & the `password`
type TLoginEvent = {
  type: 'LOGIN'
  payload: TFetchUserTokenParams
}

// directly set the user auth token & user ID
// e.g. obtained from mutation after password change
type TSetAuthDataEvent = {
  type: 'SET_AUTH_DATA'
  payload: Pick<TUserContext, 'userToken' | 'userId'>
}

// clear login error
type TClearError = {
  type: 'CLEAR_ERROR'
}

// log user out by replacing their auth token
// with the newly obtained guest token
type TLogOutEvent = { type: 'LOGOUT' }

type TEvent = TInternalEvents | TLoginEvent | TLogOutEvent | TSetAuthDataEvent | TClearError

export type TAuthMachineTypes = TMachineTypes<TBaseContext, TStateContextMap, TEvent>
